import { createContext, useContext, useEffect, useState } from "react";
import type { ApiEndpoints } from "@avisoma//shared";
import {
  isThemeStorageEvent,
  themeModeFromStorageValue,
  type ThemeMode,
} from "../lib/themeMode";

export type { ThemeMode };

type Response = ApiEndpoints<string>["/api/auth/me"]["GET"]["response"];
export type User = Response["user"];

interface AuthContextType {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<AuthContextType | null>(null);

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [modeSetting, setModeSetting] = useState<ThemeMode>(() =>
    themeModeFromStorageValue(localStorage.theme)
  );
  const [systemSetting, setSystemSetting] = useState<"dark" | "light">(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    return mediaQuery.matches ? "dark" : "light";
  });

  useEffect(() => {
    const metaThemeColor = document.querySelector("meta[name='theme-color']");
    const metaColorScheme = document.querySelector("meta[name='color-scheme']");
    if (modeSetting === "system") {
      document.documentElement.classList.toggle(
        "dark",
        systemSetting === "dark"
      );
      metaThemeColor?.setAttribute("content", "dark light");
      metaColorScheme?.setAttribute("content", "dark light");
    } else {
      document.documentElement.classList.toggle("dark", modeSetting === "dark");
      metaThemeColor?.setAttribute(
        "content",
        modeSetting === "dark" ? "#09090b" : "#f4f4f5"
      );
      metaColorScheme?.setAttribute(
        "content",
        modeSetting === "dark" ? "dark" : "light"
      );
    }
  }, [modeSetting, systemSetting]);

  useEffect(() => {
    const abortController = new AbortController();
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    mediaQuery.addEventListener(
      "change",
      (e) => {
        if (e.matches) {
          setSystemSetting("dark");
        } else {
          setSystemSetting("light");
        }
      },
      {
        signal: abortController.signal,
      }
    );

    // Initial check
    if (mediaQuery.matches) {
      setSystemSetting("dark");
    } else {
      setSystemSetting("light");
    }

    return () => {
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    window.addEventListener(
      "storage",
      (event) => {
        if (!isThemeStorageEvent(event, window.localStorage)) return;
        setModeSetting(themeModeFromStorageValue(localStorage.theme));
      },
      {
        signal: abortController.signal,
      }
    );
    setModeSetting(themeModeFromStorageValue(localStorage.theme));
    return () => {
      abortController.abort();
    };
  }, []);

  const setMode = (mode: ThemeMode) => {
    localStorage.theme = mode;
    setModeSetting(mode);
  };

  return (
    <ThemeContext.Provider
      value={{
        mode: modeSetting,
        setMode,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within an ThemeProvider");
  }
  return context;
};
